import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CampaignRounded from '@mui/icons-material/CampaignRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import PhoneIphoneRounded from '@mui/icons-material/PhoneIphoneRounded';
import { ApiError } from '@/api';
import { useNotify } from '@/components/feedback/notificationsContext';
import { useEu } from './EuContext';
import { mudaCelular, mudaOfertas } from './service';

/** Liga/desliga, com um clique, o aceite de receber ofertas de conteúdo do professor. */
export function useOfertas() {
  const { eu, recarrega } = useEu();
  const notify = useNotify();
  const [local, setLocal] = useState<boolean | null>(null);
  const [salvando, setSalvando] = useState(false);
  const ligado = local ?? eu.vinculo?.ofertas ?? false;

  const alterna = async () => {
    const novo = !ligado;
    setLocal(novo); // muda na hora; volta se der erro
    setSalvando(true);
    try {
      await mudaOfertas(novo);
      notify(
        novo
          ? 'Pronto: você vai receber as novidades.'
          : 'Pronto: você não vai mais receber as novidades.',
      );
      recarrega();
    } catch {
      setLocal(!novo);
      notify('Não deu para salvar agora. Tente de novo.');
    } finally {
      setSalvando(false);
    }
  };
  return { ligado, alterna, salvando, disponivel: Boolean(eu.vinculo) };
}

/** "+5511987654321" → "(11) 98765-4321" */
export function formataCelular(v: string | null | undefined) {
  const d = (v ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return v ?? '';
}

/** Máscara enquanto digita: (11) 98765-4321 */
function mascara(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** Celular opcional para contatos eventuais: salvar, ligar/desligar o contato e apagar, tudo aqui. */
function BlocoCelular() {
  const { eu, recarrega } = useEu();
  const notify = useNotify();
  const salvo = eu.vinculo?.celular ?? null;
  const [contato, setContato] = useState<boolean>(eu.vinculo?.celularContato ?? false);
  const [texto, setTexto] = useState(formataCelular(salvo));
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const editado = texto.replace(/\D/g, '') !== formataCelular(salvo).replace(/\D/g, '');

  const salva = async (celular: string | null, ligado: boolean, msg: string) => {
    setSalvando(true);
    setErro(null);
    try {
      const r = await mudaCelular(celular, ligado);
      setContato(r.celularContato);
      setTexto(formataCelular(r.celular));
      notify(msg);
      recarrega();
    } catch (e) {
      setErro(
        (e instanceof ApiError && (e.data as { message?: string })?.message) ||
          'Não deu para salvar agora.',
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ alignItems: { sm: 'flex-start' } }}
    >
      <PhoneIphoneRounded color="primary" sx={{ mt: { sm: 0.5 } }} />
      <Stack spacing={1.5} sx={{ flex: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Celular para contatos eventuais{' '}
            <Typography component="span" color="text.secondary">
              (opcional)
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Se quiser, deixe um celular para o Prof. Bossini falar com você: avisos sobre notas e
            entregas, notícias de informática, divulgação de cursos e oportunidades que possam te
            interessar. Ligue ou desligue quando quiser: desligado, o professor não vê o número.
            Você também pode apagar o número a qualquer momento.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ alignItems: { sm: 'flex-start' } }}
        >
          <TextField
            size="small"
            label="Celular com DDD"
            placeholder="(11) 98765-4321"
            inputMode="tel"
            value={texto}
            onChange={(e) => setTexto(mascara(e.target.value))}
            error={Boolean(erro)}
            helperText={erro ?? ' '}
            sx={{ minWidth: 220 }}
          />
          {editado && (
            <Button
              variant="contained"
              disabled={salvando || !texto}
              onClick={() => void salva(texto, true, 'Celular salvo e contato ligado.')}
              sx={{ mt: { sm: 0.25 } }}
            >
              Salvar
            </Button>
          )}
          {salvo && !editado && (
            <Button
              color="warning"
              disabled={salvando}
              onClick={() => void salva(null, false, 'Celular apagado.')}
              sx={{ mt: { sm: 0.25 } }}
            >
              Apagar número
            </Button>
          )}
        </Stack>
        {salvo && (
          <FormControlLabel
            control={
              <Switch
                checked={contato}
                disabled={salvando || editado}
                onChange={() =>
                  void salva(
                    salvo,
                    !contato,
                    !contato
                      ? 'Contato por celular ligado.'
                      : 'Contato por celular desligado: o professor não vê mais o número.',
                  )
                }
              />
            }
            label={
              contato
                ? 'O professor pode me contatar por este celular'
                : 'Contato por celular desligado'
            }
          />
        )}
      </Stack>
    </Stack>
  );
}

/** "Seus contatos": ofertas de conteúdo por e-mail e celular opcional, cada um com um clique. */
export function CartaoOfertas() {
  const { ligado, alterna, salvando, disponivel } = useOfertas();
  if (!disponivel) return null;
  return (
    <Card variant="outlined" id="seus-contatos">
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Seus contatos
        </Typography>
        <Stack spacing={2.5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ alignItems: { sm: 'center' } }}
          >
            <CampaignRounded color="primary" />
            <Stack sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Novidades do Prof. Bossini no seu e-mail
              </Typography>
              <Typography variant="body2" color="text.secondary">
                De vez em quando, o Prof. Bossini manda coisas que podem fazer diferença na sua
                formação: notícias de informática e tecnologia, dicas de carreira, divulgação de
                cursos, eventos e oportunidades, e avisos sobre as suas notas. Nada de spam: você
                liga ou desliga quando quiser, aqui mesmo, e todo e-mail traz um link para sair com
                um clique.
              </Typography>
            </Stack>
            <FormControlLabel
              control={
                <Switch checked={ligado} disabled={salvando} onChange={() => void alterna()} />
              }
              label={ligado ? 'Quero receber' : 'Não quero receber'}
              sx={{ flexShrink: 0, mr: 0 }}
            />
          </Stack>
          <Divider />
          <BlocoCelular />
          <Alert icon={<LockRounded fontSize="small" />} severity="info" variant="outlined">
            Só o professor vê esses dados, eles não mudam nada nas suas notas e nunca são repassados
            a ninguém.
          </Alert>
        </Stack>
      </CardContent>
    </Card>
  );
}
